import { safeLocalStorage } from './safeStorage';
/**
 * Notification Sound Utility for Wefaqna Marriage App
 *
 * Professional, warm, and trustworthy sounds designed for a marriage matchmaking platform.
 * Uses Web Audio API to generate pleasant notification sounds.
 *
 * Sound Types:
 * - message: Warm, gentle chime for new messages (intimate, welcoming)
 * - match: Celebratory ascending tone for new matches (exciting, hopeful)
 * - notification: Soft bell for general notifications (professional, trustworthy)
 * - admin: Formal tone for admin/system messages (authoritative, reassuring)
 * - success: Pleasant confirmation sound (positive, affirming)
 */

// Storage key for sound preferences
const SOUND_ENABLED_KEY = 'wefaqna_notification_sound_enabled';

// Audio context (shared)
let audioContext = null;

/**
 * Get or create audio context
 */
const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume if suspended (browsers require user interaction)
  if (audioContext.state === 'suspended') {
    // `resume()` is a promise, and iOS Safari REJECTS it ("InvalidStateError:
    // Failed to start the audio device") when the page cannot take the audio
    // device — backgrounded, or a call in progress. Uncaught, that reached
    // Sentry as an unhandled rejection on /messages (WEFAQNA-FRONTEND-S). A
    // chime is best-effort: a message that arrives silently is still there.
    audioContext.resume().catch(() => {});
  }
  return audioContext;
};

/**
 * Create a warm bell-like tone with harmonics
 * @param {AudioContext} ctx - Audio context
 * @param {number} frequency - Base frequency
 * @param {number} startTime - Start time
 * @param {number} duration - Duration in seconds
 * @param {number} volume - Volume (0-1)
 */
const createWarmTone = (ctx, frequency, startTime, duration, volume = 0.15) => {
  // Main tone (sine - pure and warm)
  const osc1 = ctx.createOscillator();
  const gain1 = ctx.createGain();
  osc1.type = 'sine';
  osc1.frequency.value = frequency;

  // Soft harmonic (one octave up, quieter) for richness
  const osc2 = ctx.createOscillator();
  const gain2 = ctx.createGain();
  osc2.type = 'sine';
  osc2.frequency.value = frequency * 2;

  // Connect
  osc1.connect(gain1);
  osc2.connect(gain2);
  gain1.connect(ctx.destination);
  gain2.connect(ctx.destination);

  // Envelope for main tone - soft attack, gentle decay
  gain1.gain.setValueAtTime(0, startTime);
  gain1.gain.linearRampToValueAtTime(volume, startTime + 0.03);
  gain1.gain.exponentialRampToValueAtTime(volume * 0.6, startTime + duration * 0.3);
  gain1.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  // Envelope for harmonic - even softer
  gain2.gain.setValueAtTime(0, startTime);
  gain2.gain.linearRampToValueAtTime(volume * 0.2, startTime + 0.02);
  gain2.gain.exponentialRampToValueAtTime(0.001, startTime + duration * 0.7);

  osc1.start(startTime);
  osc1.stop(startTime + duration);
  osc2.start(startTime);
  osc2.stop(startTime + duration);
};

/**
 * Play notification sounds based on type
 * @param {string} type - Sound type
 */
const playTone = (type = 'notification') => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    switch (type) {
      case 'message':
        // New Message: Warm, gentle two-note chime
        // Like a soft doorbell - welcoming and intimate
        // Notes: G4 -> B4 (major third, warm and pleasant)
        createWarmTone(ctx, 392.00, now, 0.4, 0.18);        // G4
        createWarmTone(ctx, 493.88, now + 0.15, 0.35, 0.15); // B4
        break;

      case 'match':
        // New Match/Contact Request: Celebratory ascending chord
        // Hopeful and exciting - like finding a connection
        // Notes: C4 -> E4 -> G4 (C major arpeggio - universally positive)
        createWarmTone(ctx, 261.63, now, 0.5, 0.15);        // C4
        createWarmTone(ctx, 329.63, now + 0.12, 0.45, 0.14); // E4
        createWarmTone(ctx, 392.00, now + 0.24, 0.5, 0.16);  // G4
        break;

      case 'notification':
        // General Notification: Soft, professional single bell
        // Trustworthy and unobtrusive
        // Note: E4 with gentle resonance
        createWarmTone(ctx, 329.63, now, 0.5, 0.16);  // E4
        break;

      case 'admin':
        // Admin/System Message: Formal but warm two-tone
        // Authoritative yet reassuring
        // Notes: D4 -> A4 (perfect fifth - stable, trustworthy)
        createWarmTone(ctx, 293.66, now, 0.45, 0.14);       // D4
        createWarmTone(ctx, 440.00, now + 0.18, 0.4, 0.12); // A4
        break;

      case 'success':
        // Success/Confirmation: Pleasant rising confirmation
        // Positive and affirming
        // Notes: G4 -> C5 (perfect fourth up - resolved, complete)
        createWarmTone(ctx, 392.00, now, 0.35, 0.14);       // G4
        createWarmTone(ctx, 523.25, now + 0.1, 0.45, 0.16); // C5
        break;

      case 'error':
        // Error/Warning: Gentle attention-getter
        // Not alarming, but noticeable
        // Notes: E4 -> Eb4 (minor second down - subtle concern)
        createWarmTone(ctx, 329.63, now, 0.3, 0.12);        // E4
        createWarmTone(ctx, 311.13, now + 0.15, 0.35, 0.10); // Eb4
        break;

      default:
        // Default to notification sound
        createWarmTone(ctx, 329.63, now, 0.5, 0.16);
    }

    return true;
  } catch (error) {
    // Silently fail if notification sound cannot be played
    return false;
  }
};

/**
 * Check if notification sound is enabled
 * @returns {boolean} True if sound is enabled
 */
export const isSoundEnabled = () => {
  const stored = safeLocalStorage.getItem(SOUND_ENABLED_KEY);
  // Default to enabled if not set
  return stored === null ? true : stored === 'true';
};

/**
 * Set notification sound preference
 * @param {boolean} enabled - Whether to enable notification sounds
 */
export const setSoundEnabled = (enabled) => {
  safeLocalStorage.setItem(SOUND_ENABLED_KEY, enabled.toString());
};

/**
 * Play notification sound
 * @param {string} type - Type of notification:
 *   - 'message': New message (warm chime)
 *   - 'match': New match/contact request (celebratory)
 *   - 'notification': General notification (soft bell)
 *   - 'admin': Admin/system message (formal)
 *   - 'success': Success confirmation (positive)
 *   - 'error': Error/warning (gentle alert)
 * @returns {boolean} Success status
 */
export const playNotificationSound = (type = 'notification') => {
  // Check if sound is enabled
  if (!isSoundEnabled()) {
    return false;
  }

  return playTone(type);
};

/**
 * Play a test sound to preview the notification sound
 * @param {string} type - Type of sound to test
 * @returns {boolean} Success status
 */
export const playTestSound = (type = 'notification') => {
  return playTone(type);
};

/**
 * Play all sounds in sequence (for testing/demo)
 * @returns {Promise<void>}
 */
export const playAllSounds = async () => {
  const types = ['notification', 'message', 'match', 'admin', 'success'];
  for (const type of types) {
    playTone(type);
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
};

export default {
  isSoundEnabled,
  setSoundEnabled,
  playNotificationSound,
  playTestSound,
  playAllSounds,
};
